import React, { useEffect, useRef, useState } from "react";
import "./NcfTraining.scss";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import { ArrowUpRight } from "iconoir-react";
import { Send, CheckCircle, Phone, Mail, MapPin } from "lucide-react";
import {
  FaCheckCircle, FaUsers, FaGraduationCap, FaChartLine,
  FaBookOpen, FaUserTie, FaSchool, FaBullseye, FaTrophy,
  FaStar, FaCogs, FaLaptopCode, FaHandshake,
} from "react-icons/fa";
import emailjs from "@emailjs/browser";
import AOS from "aos";
import "aos/dist/aos.css";
import imgKvs      from "../assets/incubation/img-080.jpg";
import imgNavodaya from "../assets/incubation/img-082.png";
import imgBeo      from "../assets/incubation/img-083.jpg";
import imgSkill    from "../assets/incubation/img-084.jpg";
import logoIhfc     from "../assets/incubation/logo-ihfc.svg";
import logoMnnit    from "../assets/incubation/logo-mnnit.png";
import logoKvs      from "../assets/incubation/logo-kvs.png";
import logoNavodaya from "../assets/incubation/logo-navodaya.png";
import logoEklavya  from "../assets/incubation/logo-emrs.jpg";
import partnerScert from "../assets/partners/l-scert_11zon.webp";
import partnerSiemt from "../assets/partners/l-siemt_11zon.webp";
import logoPmshri   from "../assets/incubation/logo-pmshri.png";
import logoSamagra  from "../assets/incubation/logo-samagra.png";
import AsOfNote from "../components/AsOfNote";
import { impact } from "../data/stats";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ── Data ──────────────────────────────────────────────────────────────────────

const PROGRAMS = [
  {
    title: "KVS Teachers Training",
    stat: "8,000+",
    statLabel: "Teachers Trained",
    desc: "Empowering Kendriya Vidyalaya teachers with smart classroom techniques, Medha AI tools, and NCF-aligned pedagogical practices.",
    photo: imgKvs,
    tag: "Government Schools",
  },
  {
    title: "Navodaya Teachers Training",
    stat: "400+",
    statLabel: "JNV Teachers",
    desc: "In-depth NCF 2023 and NEP 2020 aligned training for Jawahar Navodaya Vidyalaya teachers — transforming residential school classrooms.",
    photo: imgNavodaya,
    tag: "Residential Schools",
  },
  {
    title: "BEO Training Program",
    stat: "95",
    statLabel: "Officers Certified",
    desc: "Block Education Officers trained on Medha AI and GenAI tools — equipping policy-level leaders to drive school transformation at scale.",
    photo: imgBeo,
    tag: "Education Officers",
  },
  {
    title: "Skill Enhancement Program",
    // 50,000 is what the Incubation page reports for this same programme. It
    // previously read 3,00,000+ here, which was the company-wide student
    // total pasted into a programme slot.
    stat: "50,000+",
    statLabel: "Students Trained",
    desc: "Preparing students for the future — 21st-century skills, emerging technologies, and career-readiness built into a structured curriculum.",
    photo: imgSkill,
    tag: "Student Skilling",
  },
];

const MODULES = [
  {
    icon: <FaBookOpen />,
    title: "NCF 2023 Deep-Dive",
    desc: "Understand the complete NCF 2023 framework — its philosophy, competencies, and classroom implementation from day one.",
  },
  {
    icon: <FaGraduationCap />,
    title: "NEP 2020 Implementation",
    desc: "Translate NEP 2020 policy into everyday classroom practice with practical tools, templates, and real case studies.",
  },
  {
    icon: <FaBullseye />,
    title: "Competency-Based Learning",
    desc: "Shift from rote memorization to outcome-focused teaching — design lessons around student competencies, not content coverage.",
  },
  {
    icon: <FaLaptopCode />,
    title: "AI Tools in the Classroom",
    desc: "Hands-on training with Medha AI — India's first teacher assistive AI platform, built for Indian classrooms and teachers.",
  },
  {
    icon: <FaCogs />,
    title: "Assessment Reform",
    desc: "Design assessments that measure real learning — holistic, formative, and fully aligned with NCF's evaluation framework.",
  },
  {
    icon: <FaUsers />,
    title: "Inclusive Education",
    desc: "Build classrooms where every learner thrives — strategies for diverse needs, multilingual contexts, and equity-first teaching.",
  },
];

const AUDIENCE = [
  {
    icon: <FaSchool />,
    title: "Government School Teachers",
    desc: "KVS, NVS, EMRS, and state government school teachers seeking NCF 2023 and NEP 2020 certification and practical upskilling.",
    tags: ["KVS", "NVS", "EMRS", "State Schools"],
  },
  {
    icon: <FaUserTie />,
    title: "School Administrators & BEOs",
    desc: "Principals, vice-principals, and Block Education Officers driving school-wide transformation and policy implementation at scale.",
    tags: ["Principals", "BEOs", "DEOs", "Leaders"],
  },
  {
    icon: <FaGraduationCap />,
    title: "Private School Educators",
    desc: "CBSE, ICSE, and private school teachers aligning their practice with national curriculum standards and the NEP 2020 vision.",
    tags: ["CBSE", "ICSE", "Private Schools"],
  },
];

const STEPS = [
  {
    num: "01",
    icon: <FaHandshake />,
    title: "Institution Registration",
    desc: "Your school or institution registers and shares basic details about the team size and training requirements.",
  },
  {
    num: "02",
    icon: <FaBullseye />,
    title: "Needs Assessment",
    desc: "Our academic team conducts a baseline assessment to tailor training to your teachers' specific gaps and goals.",
  },
  {
    num: "03",
    icon: <FaGraduationCap />,
    title: "Training Delivery",
    desc: "Structured on-site or virtual sessions with hands-on practice, real case studies, and expert CoGrad trainers.",
  },
  {
    num: "04",
    icon: <FaChartLine />,
    title: "Certification & Support",
    desc: "Teachers receive NCF-certified credentials and ongoing post-training support to implement learnings in their classrooms.",
  },
];

const TESTIMONIALS = [
  {
    text: "The NCF training by CoGrad completely changed how I approach lesson planning. The Medha AI tools have made my classes far more engaging and effective for my students.",
    author: "Renu Sharma",
    role: "Teacher · Kendriya Vidyalaya, Delhi",
    initials: "RS",
  },
  {
    text: "As a Block Education Officer, this training gave me a practical framework to guide all schools in my block. The GenAI tools session was particularly eye-opening.",
    author: "Pramod Kumar Singh",
    role: "Block Education Officer · Uttar Pradesh",
    initials: "PK",
  },
  {
    text: "I had heard about NEP 2020 for years but never knew how to apply it in my classroom. CoGrad gave me clarity, tools, and the confidence to actually do it.",
    author: "Anita Verma",
    role: "Senior Teacher · Navodaya Vidyalaya",
    initials: "AV",
  },
];

const PARTNERS = [
  { logo: logoKvs,      name: "Kendriya Vidyalaya Sangathan",  tag: "Central Schools"      },
  { logo: logoNavodaya, name: "Navodaya Vidyalaya Samiti",     tag: "Residential Schools"  },
  { logo: logoEklavya,  name: "EMRS",                          tag: "Tribal Schools"        },
  { logo: partnerScert, name: "SCERT",                         tag: "State Education"       },
  { logo: partnerSiemt, name: "SIEMT",                         tag: "Teacher Training"      },
  { logo: logoIhfc,     name: "IHFC — IIT Delhi",              tag: "Research Institute"    },
  { logo: logoMnnit,    name: "MNNIT Allahabad",               tag: "Technical University"  },
  { logo: logoPmshri,   name: "PM SHRI Schools",               tag: "Flagship Scheme"       },
  { logo: logoSamagra,  name: "Samagra Shiksha",               tag: "Govt. Initiative"      },
];

const MARQUEE_ITEMS = [
  "NCF 2023 Certified",
  "NEP 2020 Aligned",
  "10,000+ Teachers Trained",
  "KVS & NVS Programs",
  "Medha AI Integration",
  "Pan-India Training",
  "Government Certified",
  "Competency-Based Learning",
];

const ABOUT_CARDS = [
  {
    icon: <FaBookOpen />,
    title: "NCF 2023",
    body: "India's new curriculum framework replacing NCF 2005 — student-centred, competency-based, rooted in Indian values and knowledge.",
  },
  {
    icon: <FaGraduationCap />,
    title: "NEP 2020",
    body: "National Education Policy 2020 — a 5+3+3+4 structure that reimagines school education from the ground up.",
  },
  {
    icon: <FaLaptopCode />,
    title: "Medha AI",
    body: "CoGrad's AI teaching assistant — built for Indian classrooms, used daily by NCF-trained teachers across the country.",
  },
  {
    icon: <FaTrophy />,
    title: "Certified",
    body: "CoGrad-certified NCF training recognized by partner institutions including KVS, NVS, SCERT, and SIEMT.",
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  institutionName: "", contactName: "", email: "",
  phone: "", city: "", institutionType: "",
  teacherCount: "", trainingMode: "", message: "",
};

const NcfTraining = () => {
  const formRef  = useRef(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [errors,      setErrors]      = useState({});
  const [isLoading,   setIsLoading]   = useState(false);
  const [sent,        setSent]        = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    AOS.init({ duration: 500, once: true, offset: 30, easing: 'ease-out' });
    const t = setTimeout(() => AOS.refreshHard(), 100);
    window.scrollTo(0, 0);
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    return () => clearTimeout(t);
  }, []);

  const handleRegister = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.institutionName.trim()) e.institutionName = "Institution name is required";
    if (!form.contactName.trim())     e.contactName     = "Contact name is required";
    if (!form.email.trim())           e.email           = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.phone.trim())           e.phone           = "Phone number is required";
    if (!form.institutionType)        e.institutionType = "Please select institution type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setIsLoading(true);
    try {
      // Save to MongoDB — always
      const res = await fetch(`${API_URL}/api/ncf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          institutionName: form.institutionName,
          contactName:     form.contactName,
          email:           form.email,
          phone:           form.phone,
          city:            form.city,
          institutionType: form.institutionType,
          teacherCount:    form.teacherCount,
          trainingMode:    form.trainingMode,
          message:         form.message,
        }),
      });
      if (!res.ok) throw new Error("Server error");

      // Try EmailJS — ignore if it fails
      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_name: "Admin",
          message: `NCF Training Registration\n\nInstitution: ${form.institutionName}\nContact: ${form.contactName}\nEmail: ${form.email}\nPhone: ${form.phone}\nCity: ${form.city || "—"}\nType: ${form.institutionType}\nTeachers: ${form.teacherCount || "—"}\nMode: ${form.trainingMode || "—"}\nMessage: ${form.message || "—"}`,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      ).catch(() => {});

      setSent(true);
      setForm(EMPTY_FORM);
    } catch {
      setSubmitError("Failed to submit. Please email us at connect@cograd.in");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ncf">
      <Helmet>
        <title>NCF Teacher Training Programs | CoGrad</title>
        <meta
          name="description"
          content="CoGrad's NCF 2023 & NEP 2020 aligned teacher training programs — empowering 8,000+ teachers across KVS, NVS, and government schools across India."
        />
      </Helmet>

      <Header />

      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section className="ncf-hero">
        <div className="ncf-hero__bg">
          <div className="ncf-hero__grid" />
          <div className="ncf-hero__glow ncf-hero__glow--a" />
          <div className="ncf-hero__glow ncf-hero__glow--b" />
        </div>

        <div className="ncf-wrap ncf-hero__inner">

          {/* Left — copy */}
          <div className="ncf-hero__copy" data-aos="fade-up">
            <div className="ncf-hero__badge">
              <span className="ncf-hero__badge-dot" />
              NCF 2023 &amp; NEP 2020 Certified Training
            </div>

            <h1 className="ncf-hero__h1">
              NCF Teacher<br />
              <span className="ncf-accent">Training</span> Programs
            </h1>

            <p className="ncf-hero__sub">
              Empowering India's teachers with National Curriculum Framework
              aligned training — practical, impactful, and designed for the
              real Indian classroom.
            </p>

            <div className="ncf-hero__cta">
              <button className="ncf-btn ncf-btn--primary" onClick={handleRegister}>
                Register Your Institution <ArrowUpRight width={18} height={18} />
              </button>
              <a href="#programs" className="ncf-btn ncf-btn--ghost">
                View Programs
              </a>
            </div>

            <div className="ncf-hero__stats">
              {[
                { n: impact.teachers.display, l: impact.teachers.label, icon: <FaGraduationCap /> },
                { n: "100+",                  l: "Programs",            icon: <FaBookOpen />      },
                { n: impact.schools.display,  l: impact.schools.label,  icon: <FaSchool />        },
              ].map((s) => (
                <div className="ncf-hero__stat" key={s.n}>
                  <div className="ncf-hero__stat-icon">{s.icon}</div>
                  <span className="ncf-hero__stat-n">{s.n}</span>
                  <span className="ncf-hero__stat-l">{s.l}</span>
                </div>
              ))}
            </div>
            <AsOfNote className="ncf-hero__asof" />
          </div>

          {/* Right — visual */}
          <div className="ncf-hero__visual" data-aos="fade-left" data-aos-delay="140">
            <div className="ncf-hero__img-wrap">
              <img src={imgKvs} alt="CoGrad NCF Teacher Training Session" />
              <div className="ncf-hero__img-veil" />
              <div className="ncf-hero__float-badge ncf-hero__float-badge--tl">
                <FaTrophy />
                <span>Govt. Certified</span>
              </div>
              <div className="ncf-hero__float-badge ncf-hero__float-badge--br">
                <FaUsers />
                <span>8,000+ Trained</span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ══ MARQUEE ══════════════════════════════════════════════════════════ */}
      <div className="ncf-marquee" aria-hidden="true">
        <div className="ncf-marquee__track">
          {[0, 1].map((k) => (
            <div key={k} className="ncf-marquee__row">
              {MARQUEE_ITEMS.map((item, i) => (
                <React.Fragment key={i}>
                  <span>{item}</span>
                  <span className="ncf-marquee__sep">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ══ ABOUT NCF ════════════════════════════════════════════════════════ */}
      <section className="ncf-about">
        <div className="ncf-wrap ncf-about__grid">

          <div className="ncf-about__copy" data-aos="fade-right">
            <span className="ncf-tag">What Is NCF Training</span>
            <h2 className="ncf-h2">
              Aligning India's teachers with <span className="ncf-accent">NCF 2023 &amp; NEP 2020</span>
            </h2>
            <p className="ncf-about__body">
              The National Curriculum Framework 2023 is India's most comprehensive
              education reform in decades. CoGrad translates this policy into
              practical, classroom-ready training that teachers can implement
              from day one — no jargon, just results.
            </p>
            <ul className="ncf-checklist">
              <li><FaCheckCircle /><span>Competency-based learning aligned with NCF 2023</span></li>
              <li><FaCheckCircle /><span>AI-integrated classroom strategies via Medha AI</span></li>
              <li><FaCheckCircle /><span>Assessment reform aligned with holistic evaluation</span></li>
              <li><FaCheckCircle /><span>Inclusive and multilingual education practices</span></li>
            </ul>
          </div>

          <div className="ncf-about__cards" data-aos="fade-left" data-aos-delay="100">
            {ABOUT_CARDS.map((c, i) => (
              <div className="ncf-about__card" key={c.title} data-aos="fade-up" data-aos-delay={i * 60}>
                <div className="ncf-about__card-icon">{c.icon}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ PROGRAMS ═════════════════════════════════════════════════════════ */}
      <section id="programs" className="ncf-programs">
        <div className="ncf-wrap">
          <div className="ncf-section-head" data-aos="fade-up">
            <span className="ncf-tag">Our Programs</span>
            <h2 className="ncf-h2">Training built for every level</h2>
            <p className="ncf-lead">
              From classroom teachers to district officers — CoGrad delivers
              targeted NCF training at every level of India's education system.
            </p>
          </div>

          {/* Desktop grid */}
          <div className="ncf-programs__grid">
            {PROGRAMS.map((p, i) => (
              <div
                className="ncf-prog-card"
                key={p.title}
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <div className="ncf-prog-card__photo">
                  <img src={p.photo} alt={p.title} />
                  <div className="ncf-prog-card__veil" />
                  <span className="ncf-prog-card__tag">{p.tag}</span>
                  <div className="ncf-prog-card__stat">
                    <strong>{p.stat}</strong>
                    <span>{p.statLabel}</span>
                  </div>
                </div>
                <div className="ncf-prog-card__body">
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile auto-scroll ticker */}
          <div className="ncf-programs__viewport">
            <div className="ncf-programs__track">
              {[...PROGRAMS, ...PROGRAMS].map((p, i) => (
                <div className="ncf-prog-card" key={i}>
                  <div className="ncf-prog-card__photo">
                    <img src={p.photo} alt={p.title} />
                    <div className="ncf-prog-card__veil" />
                    <span className="ncf-prog-card__tag">{p.tag}</span>
                    <div className="ncf-prog-card__stat">
                      <strong>{p.stat}</strong>
                      <span>{p.statLabel}</span>
                    </div>
                  </div>
                  <div className="ncf-prog-card__body">
                    <h3>{p.title}</h3>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CURRICULUM ═══════════════════════════════════════════════════════ */}
      <section className="ncf-curriculum">
        <div className="ncf-wrap">
          <div className="ncf-section-head" data-aos="fade-up">
            <span className="ncf-tag">Training Modules</span>
            <h2 className="ncf-h2">What teachers learn</h2>
            <p className="ncf-lead">
              A practical, modular curriculum designed by academic experts and
              aligned with the latest NCF 2023 and NEP 2020 guidelines.
            </p>
          </div>

          {/* Desktop grid */}
          <div className="ncf-curriculum__grid">
            {MODULES.map((m, i) => (
              <div
                className="ncf-module-card"
                key={m.title}
                data-aos="fade-up"
                data-aos-delay={i * 70}
              >
                <div className="ncf-module-card__num">0{i + 1}</div>
                <div className="ncf-module-card__icon">{m.icon}</div>
                <h3 className="ncf-module-card__title">{m.title}</h3>
                <p className="ncf-module-card__desc">{m.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile auto-scroll ticker */}
          <div className="ncf-curriculum__viewport">
            <div className="ncf-curriculum__track">
              {[...MODULES, ...MODULES].map((m, i) => (
                <div className="ncf-module-card" key={i}>
                  <div className="ncf-module-card__num">0{(i % MODULES.length) + 1}</div>
                  <div className="ncf-module-card__icon">{m.icon}</div>
                  <h3 className="ncf-module-card__title">{m.title}</h3>
                  <p className="ncf-module-card__desc">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ AUDIENCE ═════════════════════════════════════════════════════════ */}
      <section className="ncf-audience">
        <div className="ncf-wrap">
          <div className="ncf-section-head" data-aos="fade-up">
            <span className="ncf-tag">Who It's For</span>
            <h2 className="ncf-h2">Built for every educator in India</h2>
            <p className="ncf-lead">
              Whether you're a classroom teacher, school leader, or education
              officer — there's a CoGrad NCF program designed for you.
            </p>
          </div>

          <div className="ncf-audience__grid">
            {AUDIENCE.map((a, i) => (
              <div
                className="ncf-audience-card"
                key={a.title}
                data-aos="fade-up"
                data-aos-delay={i * 100}
              >
                <div className="ncf-audience-card__icon">{a.icon}</div>
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
                <div className="ncf-audience-card__tags">
                  {a.tags.map((t) => <span key={t}>{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROCESS ══════════════════════════════════════════════════════════ */}
      <section className="ncf-process">
        <div className="ncf-wrap ncf-process__inner">

          <div className="ncf-process__copy" data-aos="fade-right">
            <span className="ncf-tag">How It Works</span>
            <h2 className="ncf-h2 ncf-h2--left">
              From registration<br />
              to certified <span className="ncf-accent">impact</span>
            </h2>
            <p className="ncf-process__sub">
              A simple 4-step journey from school registration to certified
              teachers — handled end-to-end by CoGrad's academic team.
            </p>
            <button className="ncf-btn ncf-btn--primary" onClick={handleRegister}>
              Register Now <ArrowUpRight width={18} height={18} />
            </button>
          </div>

          <div className="ncf-process__steps" data-aos="fade-left" data-aos-delay="120">
            {STEPS.map((s, i) => (
              <div className="ncf-process__step" key={s.title}>
                <div className="ncf-process__step-track">
                  <div className="ncf-process__step-dot">
                    <span>{s.num}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="ncf-process__step-line" />}
                </div>
                <div className="ncf-process__step-content">
                  <div className="ncf-process__step-icon">{s.icon}</div>
                  <div>
                    <h4>{s.title}</h4>
                    <p>{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ PARTNERS ═════════════════════════════════════════════════════════ */}
      <section className="ncf-partners">
        <div className="ncf-wrap">
          <div className="ncf-section-head" data-aos="fade-up">
            <span className="ncf-tag">Partner Institutions</span>
            <h2 className="ncf-h2">Trusted by India's leading education bodies</h2>
            <p className="ncf-lead">
              Our NCF training programs are delivered in partnership with the
              country's most credible government and academic institutions.
            </p>
          </div>
          {/* Desktop grid */}
          <div className="ncf-partners__grid">
            {PARTNERS.map((p, i) => (
              <div
                className="ncf-partners__card"
                key={i}
                data-aos="fade-up"
                data-aos-delay={(i % 3) * 70}
              >
                <div className="ncf-partners__logo-box">
                  <img src={p.logo} alt={p.name} />
                </div>
                <div className="ncf-partners__info">
                  <span className="ncf-partners__name">{p.name}</span>
                  <span className="ncf-partners__badge">{p.tag}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile auto-scroll ticker */}
          <div className="ncf-partners__viewport">
            <div className="ncf-partners__track">
              {[...PARTNERS, ...PARTNERS].map((p, i) => (
                <div className="ncf-partners__card" key={i}>
                  <div className="ncf-partners__logo-box">
                    <img src={p.logo} alt={p.name} />
                  </div>
                  <div className="ncf-partners__info">
                    <span className="ncf-partners__name">{p.name}</span>
                    <span className="ncf-partners__badge">{p.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ═════════════════════════════════════════════════════ */}
      <section className="ncf-testimonials">
        <div className="ncf-wrap">
          <div className="ncf-section-head" data-aos="fade-up">
            <span className="ncf-tag">Teacher Stories</span>
            <h2 className="ncf-h2">What educators are saying</h2>
          </div>

          <div className="ncf-testimonials__grid">
            {TESTIMONIALS.map((t, i) => (
              <div
                className="ncf-tcard"
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <span className="ncf-tcard__qdeco">&ldquo;</span>
                <div className="ncf-tcard__stars">
                  {[...Array(5)].map((_, s) => <FaStar key={s} />)}
                </div>
                <p className="ncf-tcard__text">{t.text}</p>
                <div className="ncf-tcard__author">
                  <div className="ncf-tcard__avatar">{t.initials}</div>
                  <div>
                    <div className="ncf-tcard__name">{t.author}</div>
                    <div className="ncf-tcard__role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ REGISTRATION FORM ════════════════════════════════════════════════ */}
      <section className="ncf-register" ref={formRef}>
        <div className="ncf-wrap ncf-register__grid">

          {/* Left — info */}
          <div className="ncf-register__info" data-aos="fade-right">
            <span className="ncf-tag">Register Now</span>
            <h2 className="ncf-h2 ncf-h2--left">
              Register your <span className="ncf-accent">institution</span>
            </h2>
            <p className="ncf-register__sub">
              Fill in the details below and our academic team will reach out
              within 24 hours to discuss your training requirements.
            </p>

            <ul className="ncf-checklist ncf-register__checklist">
              <li><FaCheckCircle /><span>NCF 2023 certified training for all teachers</span></li>
              <li><FaCheckCircle /><span>Flexible online, offline, or hybrid delivery</span></li>
              <li><FaCheckCircle /><span>Hands-on Medha AI workshops included</span></li>
              <li><FaCheckCircle /><span>Institution-level completion certificate</span></li>
              <li><FaCheckCircle /><span>Dedicated academic support post-training</span></li>
            </ul>

            <div className="ncf-register__contact-cards">
              <a href="tel:+919220253001" className="ncf-register__contact-card">
                <div className="ncf-register__contact-icon"><Phone size={17} /></div>
                <div>
                  <span>Call Us</span>
                  <strong>+91-9220253001</strong>
                </div>
              </a>
              <a href="mailto:connect@cograd.in" className="ncf-register__contact-card">
                <div className="ncf-register__contact-icon"><Mail size={17} /></div>
                <div>
                  <span>Email Us</span>
                  <strong>connect@cograd.in</strong>
                </div>
              </a>
              <div className="ncf-register__contact-card ncf-register__contact-card--plain">
                <div className="ncf-register__contact-icon"><MapPin size={17} /></div>
                <div>
                  <span>Office</span>
                  <strong>Sector 62, Noida</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form card */}
          <div className="ncf-register__card" data-aos="fade-left" data-aos-delay="80">

            {sent ? (
              <div className="ncf-register__success">
                <div className="ncf-register__success-icon">
                  <CheckCircle size={44} strokeWidth={1.6} />
                </div>
                <h3>Registration Received!</h3>
                <p>
                  Thank you! Our academic team will contact you within 24 hours
                  to discuss your NCF training requirements.
                </p>
                <button
                  className="ncf-btn ncf-btn--ghost"
                  onClick={() => setSent(false)}
                >
                  Submit Another Registration
                </button>
              </div>
            ) : (
              <>
                <div className="ncf-register__card-head">
                  <h3>Institution Registration</h3>
                  <p>Tell us about your school and training needs.</p>
                </div>

                <form className="ncf-form" onSubmit={handleSubmit} noValidate>

                  <div className="ncf-form__row ncf-form__row--2">
                    <div className={`ncf-field${errors.institutionName ? " ncf-field--err" : ""}`}>
                      <label>Institution Name *</label>
                      <input
                        type="text" name="institutionName"
                        placeholder="e.g. KV No. 1, Delhi"
                        value={form.institutionName} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.institutionName && <span>{errors.institutionName}</span>}
                    </div>
                    <div className={`ncf-field${errors.contactName ? " ncf-field--err" : ""}`}>
                      <label>Contact Person *</label>
                      <input
                        type="text" name="contactName"
                        placeholder="e.g. Rajesh Kumar"
                        value={form.contactName} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.contactName && <span>{errors.contactName}</span>}
                    </div>
                  </div>

                  <div className="ncf-form__row ncf-form__row--2">
                    <div className={`ncf-field${errors.email ? " ncf-field--err" : ""}`}>
                      <label>Email Address *</label>
                      <input
                        type="email" name="email"
                        placeholder="you@school.edu.in"
                        value={form.email} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.email && <span>{errors.email}</span>}
                    </div>
                    <div className={`ncf-field${errors.phone ? " ncf-field--err" : ""}`}>
                      <label>Phone Number *</label>
                      <input
                        type="tel" name="phone"
                        placeholder="+91 98765 43210"
                        value={form.phone} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.phone && <span>{errors.phone}</span>}
                    </div>
                  </div>

                  <div className="ncf-form__row ncf-form__row--2">
                    <div className="ncf-field">
                      <label>City &amp; State</label>
                      <input
                        type="text" name="city"
                        placeholder="e.g. Lucknow, UP"
                        value={form.city} onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>
                    <div className={`ncf-field${errors.institutionType ? " ncf-field--err" : ""}`}>
                      <label>Institution Type *</label>
                      <select name="institutionType" value={form.institutionType} onChange={handleChange} disabled={isLoading}>
                        <option value="">Select type</option>
                        <option value="KVS">Kendriya Vidyalaya (KVS)</option>
                        <option value="NVS">Navodaya Vidyalaya (NVS)</option>
                        <option value="EMRS">Eklavya Model School (EMRS)</option>
                        <option value="State Government">State Government School</option>
                        <option value="Private CBSE">Private School (CBSE)</option>
                        <option value="Private ICSE">Private School (ICSE)</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.institutionType && <span>{errors.institutionType}</span>}
                    </div>
                  </div>

                  <div className="ncf-form__row ncf-form__row--2">
                    <div className="ncf-field">
                      <label>No. of Teachers to Train</label>
                      <input
                        type="number" name="teacherCount" min="1"
                        placeholder="e.g. 25"
                        value={form.teacherCount} onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="ncf-field">
                      <label>Preferred Training Mode</label>
                      <select name="trainingMode" value={form.trainingMode} onChange={handleChange} disabled={isLoading}>
                        <option value="">Select mode</option>
                        <option value="On-site">On-site (at our school)</option>
                        <option value="Online">Online (virtual sessions)</option>
                        <option value="Hybrid">Hybrid (both)</option>
                      </select>
                    </div>
                  </div>

                  <div className="ncf-field">
                    <label>Additional Requirements</label>
                    <textarea
                      name="message" rows={3}
                      placeholder="Any specific topics, scheduling preferences, or questions…"
                      value={form.message} onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>

                  {submitError && (
                    <div className="ncf-form__submit-err">{submitError}</div>
                  )}

                  <button
                    type="submit"
                    className={`ncf-btn ncf-btn--primary ncf-form__submit${isLoading ? " ncf-form__submit--loading" : ""}`}
                    disabled={isLoading}
                  >
                    {!isLoading && <Send size={16} />}
                    {isLoading ? "Submitting…" : "Submit Registration"}
                  </button>

                  <p className="ncf-form__note">
                    We typically respond within 24 business hours.
                  </p>
                </form>
              </>
            )}
          </div>

        </div>
      </section>

      {/* ══ CTA ══════════════════════════════════════════════════════════════ */}
      <section className="ncf-cta">
        <div className="ncf-wrap">
          <div className="ncf-cta__card" data-aos="fade-up">
            <div className="ncf-cta__bg-grid" />
            <div className="ncf-cta__inner">

              <div className="ncf-cta__copy">
                <span className="ncf-tag ncf-tag--light">Get Certified</span>
                <h2>
                  Ready to transform<br />
                  <span className="ncf-cta__hl">your teachers?</span>
                </h2>
                <p>
                  Join 8,000+ teachers who have already completed CoGrad's NCF
                  training. Register your institution today.
                </p>
                <div className="ncf-cta__btns">
                  <button className="ncf-btn ncf-btn--white" onClick={handleRegister}>
                    Register Your Institution <ArrowUpRight width={18} height={18} />
                  </button>
                  <a href="tel:+919220253001" className="ncf-btn ncf-btn--ghost-white">
                    Call Us Directly
                  </a>
                </div>
              </div>

              <div className="ncf-cta__details">
                <div className="ncf-cta__details-label">What Your Teachers Get</div>
                <ul className="ncf-cta__details-list">
                  {[
                    "NCF 2023 certified training",
                    "Hands-on Medha AI workshops",
                    "NEP 2020 implementation toolkit",
                    "Competency-based assessment guide",
                    "Ongoing post-training support",
                    "Institution completion certificate",
                  ].map((item) => (
                    <li key={item}>
                      <FaCheckCircle />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NcfTraining;
